import { Proto } from "../../../../pojo/Proto";
import { S25DateParse } from "../../../../util/core/s25.date.parse";
import { S25Util } from "../../../../util/s25-util";

/** Same anchor date and datetime storage as sp_hours.open_tm / close_tm. */
export const QUOTA_TIME_ANCHOR = "1900-01-01";

export const DOW_ABBRS = Proto.DowAbbr;
export const DAY_LABELS = Proto.DowShortLabel;

/** Empty string = all days allowed. */
export const ALL_DOW = "";

export const WEEKDAY_DOW = "MO TU WE TH FR";

export const WEEKEND_DOW = "SA SU";

export type DaySelection = readonly boolean[];

export function isAllDow(dow: string | null | undefined): boolean {
    return !normalizeDow(dow);
}

export function normalizeDow(dow: string | null | undefined): string {
    if (!dow?.trim()) {
        return ALL_DOW;
    }
    return dow
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((token) => token.toUpperCase())
        .join(" ");
}

export function selectionFromDow(dow: string | null | undefined): DaySelection {
    if (isAllDow(dow)) {
        return DAY_LABELS.map(() => true);
    }
    const tokenSet = new Set(normalizeDow(dow).split(/\s+/).filter(Boolean));
    return DOW_ABBRS.map((abbr) => tokenSet.has(abbr));
}

export function dowFromSelection(selected: readonly boolean[]): string {
    if (!selected?.length || selected.every((dayOn) => dayOn)) {
        return ALL_DOW;
    }
    const tokens: string[] = [];
    for (let dayIndex = 0; dayIndex < DOW_ABBRS.length; dayIndex++) {
        if (selected[dayIndex]) {
            tokens.push(DOW_ABBRS[dayIndex]);
        }
    }
    return tokens.join(" ");
}

function parseQuotaTimeOfDay(timeStr: string): Date | null {
    const trimmed = timeStr.trim();
    if (!trimmed) {
        return null;
    }
    let parsed = S25DateParse(trimmed);
    if (!S25Util.date.isValid(parsed, true)) {
        parsed = S25Util.date.parseTime(trimmed, true);
    }
    return S25Util.date.isValid(parsed, true) ? parsed : null;
}

/** Server datetime (1900-01-01 anchor) or HH:mm → 24h HH:mm for HTML time inputs. */
export function formatTimeForInput(serverTime: string | null | undefined): string | null {
    if (!serverTime?.trim()) {
        return null;
    }
    const parsed = parseQuotaTimeOfDay(serverTime);
    if (!parsed) {
        return null;
    }
    return S25Util.date.toTimeStrFromHours(parsed.getHours() + parsed.getMinutes() / 60, true);
}

/** Form HH:mm → 1900-01-01 datetime string for sp_hours-style storage. */
export function formatTimeForServer(inputTime: string | null | undefined): string | null {
    if (!inputTime?.trim()) {
        return null;
    }
    const parsed = parseQuotaTimeOfDay(inputTime);
    if (!parsed) {
        return null;
    }
    return `${QUOTA_TIME_ANCHOR}T${S25Util.date.toS25ISOTimeStrFromDate(parsed, false)}`;
}

export function scheduleDaysSummary(dow: string | null | undefined): string {
    if (isAllDow(dow)) {
        return "All days";
    }
    const normalized = normalizeDow(dow);
    const selectedLabels = selectionFromDow(normalized)
        .map((selected, dayIndex) => (selected ? DAY_LABELS[dayIndex] : null))
        .filter((label): label is (typeof DAY_LABELS)[number] => Boolean(label));
    if (!selectedLabels.length) {
        return "No days selected";
    }
    if (normalized === WEEKDAY_DOW) {
        return "Weekdays";
    }
    if (normalized === WEEKEND_DOW) {
        return "Weekends";
    }
    return selectedLabels.join(", ");
}

export function scheduleTimeSummary(
    start: string | null | undefined,
    end: string | null | undefined,
    formatTimeLabel: (time: string) => string,
): string {
    const startInput = formatTimeForInput(start);
    const endInput = formatTimeForInput(end);
    if (!startInput || !endInput) {
        return "";
    }
    return `${formatTimeLabel(startInput)} – ${formatTimeLabel(endInput)}`;
}

export function scheduleSummaryLabel(
    quota: {
        dow?: string | null;
        allowedTimeStart?: string | null;
        allowedTimeEnd?: string | null;
    },
    formatTimeLabel: (time: string) => string,
): string {
    const dayPart = scheduleDaysSummary(quota.dow);
    const timePart = scheduleTimeSummary(quota.allowedTimeStart, quota.allowedTimeEnd, formatTimeLabel);
    if (timePart) {
        return dayPart === "All days" ? timePart : `${dayPart}; ${timePart}`;
    }
    return dayPart === "All days" ? "" : dayPart;
}
